import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Shopify } from '@/components/icons/shopify';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { TableCell, TableRow } from '@/components/ui/table';
import { formatLocalizedDate, parseDateValue } from '@/lib/date-utils';
import { DealListTab, DealState } from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { differenceInCalendarDays } from 'date-fns';
import { CalendarDays, FileSpreadsheet, Sparkles } from 'lucide-react';

import { DealStatusBadge, type DealListRow } from './deal-status-badge';

export function DealRow({
  deal,
  tab,
  onGenerate,
}: {
  deal: DealListRow;
  tab: DealListTab;
  onGenerate: (dealId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const date = parseDateValue(deal.startDate);
  const days = date ? differenceInCalendarDays(date, new Date()) : null;
  const managerName =
    deal.manager.name === 'Unknown'
      ? t('deals-list.unknown')
      : deal.manager.name;
  const url = `/deal/${encodeURIComponent(deal.id)}`;
  return (
    <TableRow className="relative cursor-pointer">
      <TableCell className="w-[58%] px-5 py-4">
        <Link
          to={url}
          state={{ fromDealsList: true }}
          onClick={() =>
            trackEvent('DEALS_LIST_DEAL_OPENED', {
              dealId: deal.id,
              tab,
              isOfferControlFlow: deal.isOfferControlFlow,
              hasNegotiationFile: !!deal.negotiationSheetUrl,
            })
          }
          className="font-semibold after:absolute after:inset-0 focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-ring"
        >
          {deal.dealName}
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <CalendarDays size={14} />
          <span>{date ? formatLocalizedDate(date, i18n.language) : '—'}</span>
          {days !== null && (
            <span>
              ·{' '}
              {t(days >= 0 ? 'deals-list.inDays' : 'deals-list.daysAgo', {
                count: Math.abs(days),
              })}
            </span>
          )}
          {deal.isShopifyPossible && (
            <Shopify
              className="h-4 w-4 shrink-0"
              fill="currentColor"
              role="img"
              aria-label="Shopify"
            />
          )}
          <Avatar className="ml-1 h-5 w-5 text-[9px]">
            <AvatarImage src={deal.manager.photoUrl ?? undefined} alt="" />
            <AvatarFallback>
              {managerName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span>{managerName}</span>
        </div>
      </TableCell>
      <TableCell className="px-5">
        <DealStatusBadge deal={deal} />
      </TableCell>
      <TableCell className="px-5">
        <div className="flex justify-end gap-2">
          {deal.state !== DealState.NegotiationClosed &&
            deal.negotiationSheetUrl && (
              <Button asChild variant="outline" className="relative z-10">
                <a
                  href={deal.negotiationSheetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackEvent('NEGOTIATION_FILE_OPENED', {
                      dealId: deal.id,
                      surface: 'deals_list',
                    })
                  }
                >
                  <FileSpreadsheet aria-hidden />
                  {t('negotiation-file.open')}
                </a>
              </Button>
            )}
          {deal.state === DealState.Empty && (
            <Button
              variant="default"
              className="relative z-10"
              onClick={() => {
                trackEvent('NEGOTIATION_FILE_GENERATION_STARTED', {
                  dealId: deal.id,
                  surface: 'deals_list',
                  isRegeneration: !!deal.negotiationSheetUrl,
                  method: 'import',
                });
                onGenerate(deal.id);
              }}
            >
              <Sparkles />
              {t(
                deal.negotiationSheetUrl
                  ? 'negotiation-file.regenerate'
                  : 'deals-list.actions.generate'
              )}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
